import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { NotificationMessage } from "../../websockets/socket";
import { getNotifications, markAllAsRead } from "../../services/partnerService";

interface NotificationState {
    notifications: NotificationMessage[];
    unreadCount: number;
    success: boolean;
    error: string | null;
    loading: boolean;
}

const initialState: NotificationState = {
    notifications: [],
    unreadCount: 0,
    success: false,
    error: null,
    loading: false,
};


export const fetchNotifications = createAsyncThunk(
    "auth/notification",
    async ( { userId, search }: { userId: string; search: string }, { rejectWithValue }) => {
        try {
            const response = await getNotifications(userId,search);
            return response;
        } catch (error: any) {
            return rejectWithValue(
                error?.response?.data?.message || "Failed to fetch notifications"
            );
        }
    }
);

export const MarkAsRead = createAsyncThunk("mark-as-read", async (userId: string, { rejectWithValue }) => {
    try {
        const response = await markAllAsRead(userId);
        return response
    } catch (error: any) {
        return rejectWithValue(
            error?.response?.data?.message || "failed to mark as read"
        )
    }
})



const notificationSlice = createSlice({
    name: "notifications",
    initialState,
    reducers: {
        addNotification: (state, action: PayloadAction<NotificationMessage>) => {
            const alreadyExists = state.notifications.some(
                (n) => n._id && n._id === action.payload._id
            );
            if (alreadyExists) return;

            state.notifications.unshift(action.payload);
            if (!action.payload.isRead) {
                state.unreadCount++;
            }
        },

        markAllRead: (state) => {
            state.notifications.forEach((n) => {
                n.isRead = true;
            });
            state.unreadCount = 0;
        },

        clearNotifications: (state) => {
            state.notifications = [];
            state.unreadCount = 0;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchNotifications.pending, (state) => {
                state.loading = true;
                state.success = false;
                state.error = null;
            })
            .addCase(fetchNotifications.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.error = null;
                state.notifications = action.payload.notifications;
                state.unreadCount = action.payload.notifications.filter(
                    (n: NotificationMessage) => !n.isRead
                ).length;
            })
            .addCase(fetchNotifications.rejected, (state, action) => {
                state.loading = false;
                state.success = false;
                state.error = (action.payload as string) || "Something went wrong";
            })
            .addCase(MarkAsRead.pending, state => {
                state.success = false;
                state.loading = true;
                state.error = null;

            })
            .addCase(MarkAsRead.fulfilled, (state, action) => {
                state.success = true;
                state.error = null;
                state.loading = false;
                state.notifications.forEach((n) => {
                    n.isRead = true
                });
                state.unreadCount = 0;
            })
            .addCase(MarkAsRead.rejected, (state, action) => {
                state.success = false;
                state.loading = false;
                state.error = (action.payload as string) || "Failed to mark as read";
            })
    },
});

export const { addNotification, markAllRead, clearNotifications } =
    notificationSlice.actions;

export default notificationSlice.reducer;